import { globalStateSelector } from "@/redux/globalState/globalStateSlice";
import { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { AppDispatch, IRootState } from "@/redux/store";
import {
  type EmptySlot as SharedEmptySlot,
  type PitchSlot as SharedPitchSlot,
  type SquadSource as SharedSquadSource,
  type SquadSwap as SharedSquadSwap,
  type SquadView as SharedSquadView,
  type SquadMaths,
  activeSquadSource,
  bestSquadSource,
  useSquadSwap as useSharedSquadSwap,
  useSquadView,
} from "@repo/shared/myClub";
import {
  bestSquadSelector,
  fetchActiveSquad,
  fetchActiveSquadStatusSelector,
  myClubActiveSquadSelector,
} from "@repo/shared/redux";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

/*
  Web's side of My Club's squads: its player type and its store. The squads
  themselves are worked out in @repo/shared/myClub, shared with the app.
*/

/** What the pitch draws for a player; both squads' players carry it. */
export type PitchPlayer = Pick<
  SquadBuilderPlayerType,
  | "resourceId"
  | "cardName"
  | "rating"
  | "preferredPosition"
  | "versionId"
  | "metalId"
  | "hasDynamicImage"
  | "playerUrl"
  | "clubId"
  | "leagueId"
  | "countryId"
  | "possiblePositions"
  | "metaRatings"
> &
  Partial<
    Pick<
      SquadBuilderPlayerType,
      "cardFont" | "foil" | "cosmetics" | "priceInfo"
    >
  > & {
    firstOwner?: boolean;
  };

export type PitchSlot = SharedPitchSlot<PitchPlayer>;
export type EmptySlot = SharedEmptySlot;
export type SquadView = SharedSquadView<PitchPlayer>;
export type SquadSource = SharedSquadSource<PitchPlayer>;
export type SquadSwap = SharedSquadSwap<PitchPlayer>;

/** The squad builder's base data, loaded at startup. */
const useSquadMaths = (): SquadMaths => {
  const { formations, chemistryProfiles, clubLinks, playerRoles } = useSelector(
    (state: IRootState) => state.defaultSquadBuilderReducer,
  );
  return useMemo(
    () => ({ formations, chemistryProfiles, clubLinks, playerRoles }),
    [formations, chemistryProfiles, clubLinks, playerRoles],
  );
};

/** The user's active squad, fetched for My Club. */
export const useActiveSquad = ({ enabled }: { enabled: boolean }) => {
  const dispatch = useDispatch<AppDispatch>();
  const squad = useSelector(myClubActiveSquadSelector);
  const status = useSelector(fetchActiveSquadStatusSelector);
  // A finished import can change the active squad: fetch it again.
  const { lastImportFinishedAt } = useSelector(globalStateSelector);

  useEffect(() => {
    if (enabled) dispatch(fetchActiveSquad());
  }, [enabled, lastImportFinishedAt, dispatch]);

  const source = useMemo<SquadSource | undefined>(
    () =>
      squad &&
      activeSquadSource({ ...squad, players: squad.players as PitchPlayer[] }),
    [squad],
  );

  return { squad: useSquadView(source, useSquadMaths()), source, status };
};

/** The best possible squad, once My Club has built it. */
export const useBestSquad = ({ name }: { name: string }) => {
  const squad = useSelector(bestSquadSelector);
  const source = useMemo<SquadSource | undefined>(
    () =>
      squad &&
      bestSquadSource(
        { ...squad, players: squad.players as unknown as PitchPlayer[] },
        name,
      ),
    [squad, name],
  );
  return useSquadView(source, useSquadMaths());
};

/** Works out swaps on the active squad; see swapSquad. */
export const useSquadSwap = (source?: SquadSource, before?: SquadView) =>
  useSharedSquadSwap(source, before, useSquadMaths());
